import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getWorkspaceSelectionKey,
  sanitizeWorkspaceSelection,
  WORKSPACE_SELECTION_STORAGE_KEY,
} from "./workspaceSelectionUtils";

const WorkspaceSelectionContext = createContext(null);

function readStoredItems() {
  try {
    return sanitizeWorkspaceSelection(
      JSON.parse(window.localStorage.getItem(WORKSPACE_SELECTION_STORAGE_KEY) || "[]")
    );
  } catch {
    return [];
  }
}

export function WorkspaceSelectionProvider({ children }) {
  const [items, setItems] = useState(readStoredItems);

  useEffect(() => {
    try {
      window.localStorage.setItem(WORKSPACE_SELECTION_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // The admin page remains usable if browser storage is unavailable.
    }
  }, [items]);

  const itemKeys = useMemo(
    () => new Set(items.map(getWorkspaceSelectionKey).filter(Boolean)),
    [items]
  );

  const isSelected = useCallback(
    (item) => itemKeys.has(getWorkspaceSelectionKey(item)),
    [itemKeys]
  );

  const toggleItem = useCallback((item) => {
    const key = getWorkspaceSelectionKey(item);
    if (!key) return;

    setItems((currentItems) => {
      const exists = currentItems.some(
        (currentItem) => getWorkspaceSelectionKey(currentItem) === key
      );

      return exists
        ? currentItems.filter((currentItem) => getWorkspaceSelectionKey(currentItem) !== key)
        : [...currentItems, item];
    });
  }, []);

  const addItems = useCallback((newItems) => {
    setItems((currentItems) => {
      const itemsByKey = new Map(
        currentItems.map((item) => [getWorkspaceSelectionKey(item), item])
      );

      sanitizeWorkspaceSelection(newItems).forEach((item) => {
        itemsByKey.set(getWorkspaceSelectionKey(item), item);
      });

      return Array.from(itemsByKey.values());
    });
  }, []);

  const removeItem = useCallback((item) => {
    const key = getWorkspaceSelectionKey(item);
    setItems((currentItems) =>
      currentItems.filter((currentItem) => getWorkspaceSelectionKey(currentItem) !== key)
    );
  }, []);

  const updateSnapshots = useCallback((updates) => {
    const updatesByKey = new Map(
      updates.map((update) => [update.key, update.snapshot || {}])
    );

    setItems((currentItems) =>
      currentItems.map((item) => {
        const snapshotPatch = updatesByKey.get(getWorkspaceSelectionKey(item));
        if (snapshotPatch && item.type === "transfer") {
          return typeof snapshotPatch.clientStatus === "boolean"
            ? { ...item, clientStatus: snapshotPatch.clientStatus }
            : item;
        }
        return snapshotPatch
          ? { ...item, snapshot: { ...item.snapshot, ...snapshotPatch } }
          : item;
      })
    );
  }, []);

  const replaceItems = useCallback((updates) => {
    const updatesByKey = new Map(updates.map((update) => [update.key, update.item]));
    setItems((currentItems) => {
      const currentKeys = new Set(currentItems.map(getWorkspaceSelectionKey));
      const replacements = sanitizeWorkspaceSelection(
        Array.from(updatesByKey.entries())
          .filter(([key]) => currentKeys.has(key))
          .map(([, item]) => item)
      );
      const replacementKeys = new Set(replacements.map(getWorkspaceSelectionKey));
      const nextItems = currentItems.map((item) => {
        const replacement = updatesByKey.get(getWorkspaceSelectionKey(item));
        return replacement || item;
      }).filter((item) => {
        const key = getWorkspaceSelectionKey(item);
        return !replacementKeys.has(key) || replacements.some((replacement) => replacement === item);
      });
      return sanitizeWorkspaceSelection([...nextItems, ...replacements]);
    });
  }, []);

  const updateComplexReferences = useCallback((updates) => {
    const complexesById = new Map(updates.map((update) => [String(update.complexId), update.complex]));
    setItems((currentItems) => sanitizeWorkspaceSelection(currentItems.map((item) => {
      const complex = complexesById.get(String(item.complexId));
      if (!complex) return item;
      if (item.type === "transfer") return { ...item, complexName: complex.name || "Без названия" };
      return {
        ...item,
        snapshot: {
          ...item.snapshot,
          complexName: complex.name || "Без названия",
          ...(item.entityType === "complex" ? {
            city: complex.city || "—",
            type: complex.type || "",
            transferNumber: complex.transfer_number ?? "",
            developer: complex.developer || "",
            rootStatus: complex.status,
            currentSipClient: complex.currentSipClient || "",
            currentSipClientId: complex.currentSipClientId || "",
          } : {}),
        },
      };
    })));
  }, []);

  const clearItems = useCallback(() => setItems([]), []);

  const value = useMemo(
    () => ({
      items,
      isSelected,
      toggleItem,
      addItems,
      updateSnapshots,
      replaceItems,
      updateComplexReferences,
      removeItem,
      clearItems,
    }),
    [addItems, clearItems, isSelected, items, removeItem, replaceItems, toggleItem, updateComplexReferences, updateSnapshots]
  );

  return (
    <WorkspaceSelectionContext.Provider value={value}>
      {children}
    </WorkspaceSelectionContext.Provider>
  );
}

export function useWorkspaceSelection() {
  const context = useContext(WorkspaceSelectionContext);

  if (!context) {
    throw new Error("useWorkspaceSelection must be used inside WorkspaceSelectionProvider");
  }

  return context;
}
